'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography } from '@mui/material';
import { Icon } from '@/components/icons';
import { APP_MAX_WIDTH } from '@/components/PageContent/PageContent';
import { Z_INDEX } from '@/constants/zIndex';
import { useDarkColors } from '@/hooks/useDarkColors';
import { getAssetUrl } from '@/utils/assetUrl';
import {
  TEMP_TEXT_BOLD_XS,
  TEMP_TEXT_BOLD_XL,
  TEMP_TEXT_MEDIUM_XS,
  TEMP_TEXT_SEMIBOLD_XS,
  TEMP_TITLE_BOLD_M,
} from '@/constants/tempTypo';
import { useWorldCupStore, type WorldCupCharacter } from '@/stores/worldCupStore';
import { useAuthStore } from '@/stores/authStore';
import { useTranslator } from '@/lang';

interface ResolvedGreeting {
  greetingText?: string | null;
  greetingVoiceUrl?: string | null;
}

const EASING: [number, number, number, number] = [0.25, 0.1, 0.25, 1];
const BATTLE_MAX_WIDTH = Math.min(APP_MAX_WIDTH - 32, 440);

export default function WorldCupBattle({
  greetings,
}: {
  greetings?: Record<string, ResolvedGreeting>;
}) {
  const candidates = useWorldCupStore((s) => s.candidates);
  const stage = useWorldCupStore((s) => s.stage);
  const roundNumber = useWorldCupStore((s) => s.roundNumber);
  const totalCount = useWorldCupStore((s) => s.totalCount);
  const deck = useWorldCupStore((s) => s.deck);
  const confirmChoice = useWorldCupStore((s) => s.confirmChoice);
  const c = useDarkColors();
  const { t, f } = useTranslator();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);
  const voiceRef = useRef<HTMLAudioElement | null>(null);

  const isFinalRound = stage === 'final';
  const seenCount = totalCount - deck.length;
  const progress = totalCount > 0 ? seenCount / totalCount : 0;

  const stopVoice = useCallback(() => {
    if (voiceRef.current) {
      voiceRef.current.pause();
      voiceRef.current = null;
    }
    setIsVoicePlaying(false);
  }, []);

  const playVoice = useCallback(
    (url: string) => {
      stopVoice();
      const audio = new Audio(url);
      voiceRef.current = audio;
      audio.play();
      setIsVoicePlaying(true);
      audio.onended = () => setIsVoicePlaying(false);
    },
    [stopVoice],
  );

  const handleSelect = useCallback(
    (character: WorldCupCharacter) => {
      const g = greetings?.[character.id];
      if (selectedId === character.id) {
        // 같은 카드 다시 탭 = 목소리 다시 듣기
        if (g?.greetingVoiceUrl) playVoice(g.greetingVoiceUrl);
        return;
      }
      stopVoice();
      setSelectedId(character.id);
      if (g?.greetingVoiceUrl) {
        playVoice(g.greetingVoiceUrl);
      }
    },
    [selectedId, greetings, stopVoice, playVoice],
  );

  const handleConfirm = useCallback(() => {
    if (!selectedId) return;
    const winner = candidates.find((ch) => ch.id === selectedId);
    if (!winner) return;
    stopVoice();
    setSelectedId(null);
    confirmChoice(winner);
  }, [selectedId, candidates, stopVoice, confirmChoice]);

  if (candidates.length === 0) return null;

  const selected = selectedId
    ? candidates.find((ch) => ch.id === selectedId) ?? null
    : null;
  const selectedGreeting = selected
    ? greetings?.[selected.id]?.greetingText?.replace(/\[.*?\]/g, '').trim() || null
    : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: Z_INDEX.MODAL,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0, 0, 0, 0.55)' }} />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'tween', duration: 0.25, ease: EASING }}
        style={{
          position: 'relative',
          width: 'calc(100% - 32px)',
          maxWidth: BATTLE_MAX_WIDTH,
          maxHeight: 'calc(100dvh - 32px)',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            bgcolor: c.bg,
            borderRadius: '24px',
            overflow: 'hidden auto',
            maxHeight: 'calc(100dvh - 32px)',
            boxShadow: c.isDark
              ? '0 8px 40px rgba(0,0,0,0.6)'
              : '0 8px 40px rgba(0,0,0,0.15)',
          }}
        >
          {/* Header */}
          <Box sx={{ px: '20px', pt: '18px', pb: '10px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: '10px',
              }}
            >
              <Box
                sx={{
                  px: '12px',
                  py: '4px',
                  borderRadius: '20px',
                  bgcolor: isFinalRound
                    ? 'rgba(255, 215, 0, 0.12)'
                    : c.isDark ? 'rgba(124, 58, 237, 0.15)' : 'rgba(75, 0, 204, 0.06)',
                  border: isFinalRound
                    ? '1px solid rgba(255, 215, 0, 0.25)'
                    : `1px solid ${c.border}`,
                }}
              >
                <Typography
                  sx={{ ...TEMP_TEXT_BOLD_XL, color: isFinalRound ? '#d4a017' : c.fg }}
                >
                  {isFinalRound
                    ? t('worldcup.final_round')
                    : f(t('worldcup.round_n'), [roundNumber])}
                </Typography>
              </Box>
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_XS, color: c.fgLower }}>
                {isFinalRound
                  ? f(t('worldcup.final_desc'), [candidates.length])
                  : f(t('worldcup.progress'), [seenCount, totalCount])}
              </Typography>
            </Box>

            <Box
              sx={{
                height: 3,
                borderRadius: 2,
                bgcolor: c.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                overflow: 'hidden',
              }}
            >
              <motion.div
                animate={{ width: `${progress * 100}%` }}
                transition={{ duration: 0.3, ease: EASING }}
                style={{ height: '100%', borderRadius: 2, background: c.primary }}
              />
            </Box>
          </Box>

          {/* Candidate grid */}
          <Box sx={{ px: '16px', pt: '4px' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`round-${roundNumber}`}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.22, ease: EASING }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                }}
              >
                {candidates.map((ch, i) => (
                  <CandidateCard
                    key={ch.id}
                    character={ch}
                    index={i}
                    isSelected={selectedId === ch.id}
                    isDimmed={!!selectedId && selectedId !== ch.id}
                    isVoicePlaying={selectedId === ch.id && isVoicePlaying}
                    onSelect={() => handleSelect(ch)}
                    colors={c}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </Box>

          {/* Bottom panel: greeting + confirm */}
          <Box sx={{ px: '16px', pt: '12px', pb: '18px' }}>
            <AnimatePresence mode="wait">
              {selected ? (
                <motion.div
                  key={`sel-${selected.id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2, ease: EASING }}
                >
                  <Box
                    sx={{
                      px: '14px',
                      py: '10px',
                      mb: '10px',
                      borderRadius: '12px',
                      bgcolor: c.isDark
                        ? 'rgba(124, 58, 237, 0.08)'
                        : 'rgba(75, 0, 204, 0.04)',
                      border: `1px solid ${
                        c.isDark ? 'rgba(124, 58, 237, 0.18)' : 'rgba(75, 0, 204, 0.08)'
                      }`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      minHeight: 44,
                    }}
                  >
                    <Typography
                      sx={{
                        ...TEMP_TEXT_MEDIUM_XS,
                        color: c.fgLow,
                        lineHeight: 1.45,
                        flex: 1,
                      }}
                    >
                      {selectedGreeting ?? characterInfoLine(selected, t, f)}
                    </Typography>
                    {isVoicePlaying ? (
                      <VoiceEqualizer color={c.primary} />
                    ) : (
                      greetings?.[selected.id]?.greetingVoiceUrl && (
                        <Icon name="icVolume" size={14} color={c.fgLower} />
                      )
                    )}
                  </Box>

                  <motion.div
                    animate={{
                      boxShadow: [
                        `0 0 0 0 ${c.isDark ? 'rgba(124,58,237,0.5)' : 'rgba(75,0,204,0.4)'}`,
                        `0 0 0 9px ${c.isDark ? 'rgba(124,58,237,0)' : 'rgba(75,0,204,0)'}`,
                        `0 0 0 0 ${c.isDark ? 'rgba(124,58,237,0)' : 'rgba(75,0,204,0)'}`,
                      ],
                    }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ borderRadius: 14 }}
                  >
                    <Box
                      onClick={handleConfirm}
                      sx={{
                        width: '100%',
                        height: 48,
                        borderRadius: '14px',
                        bgcolor: c.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                        '&:active': { transform: 'scale(0.97)' },
                        transition: 'transform 0.1s',
                      }}
                    >
                      <Icon name="icHeartFilled" size={14} color="#FFFFFF" />
                      <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: '#FFFFFF' }}>
                        {isFinalRound
                          ? f(t('worldcup.decide_final'), [displayName(selected)])
                          : f(t('worldcup.decide'), [displayName(selected)])}
                      </Typography>
                    </Box>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="hint"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Box
                    sx={{
                      height: 44 + 10 + 48,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                    }}
                  >
                    <Icon name="icVolume" size={16} color={c.fgLower} />
                    <Typography
                      sx={{ ...TEMP_TEXT_MEDIUM_XS, color: c.fgLower, textAlign: 'center' }}
                    >
                      {isFinalRound
                        ? t('worldcup.final_hint')
                        : t('worldcup.card_hint')}
                    </Typography>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>
      </motion.div>
    </motion.div>
  );
}

function displayName(character: WorldCupCharacter): string {
  return character.name?.ko || character.name?.en || '???';
}

function characterInfoLine(
  character: WorldCupCharacter,
  t: (key: string) => string,
  f: (template: string, args: (string | number)[]) => string,
): string {
  const name = displayName(character);
  const jobText = character.job?.ko || character.job?.en || '';
  const ageText = character.age ? f(t('worldcup.age_format'), [character.age]) : '';
  const info = [ageText, jobText].filter(Boolean).join(' · ');
  return info ? `${name} — ${info}` : name;
}

interface CardTag {
  label: string;
  kind: 'personality' | 'sexual';
  locked: boolean;
}

function extractCardTags(
  character: WorldCupCharacter,
  canRevealSexualTag: boolean,
): CardTag[] {
  const tags: CardTag[] = [];
  const pRaw = character.personalityTags;
  const pSrc = pRaw?.ko || pRaw?.en || pRaw;
  if (Array.isArray(pSrc)) {
    for (const t of pSrc) tags.push({ label: t, kind: 'personality', locked: false });
  }
  const sRaw = character.sexualTendencyTags;
  const sSrc = sRaw?.ko || sRaw?.en || sRaw;
  if (Array.isArray(sSrc)) {
    sSrc.forEach((t: string, i: number) =>
      tags.push({ label: t, kind: 'sexual', locked: i > 0 || !canRevealSexualTag }),
    );
  }
  return tags;
}

/** 태그 한 줄 무한 자동 슬라이드 — 내용 2벌 이어붙이고 -50%까지 이동하면 끊김 없는 루프 */
function TagMarquee({ tags, reverse }: { tags: CardTag[]; reverse?: boolean }) {
  if (tags.length === 0) return null;
  const doubled = [...tags, ...tags];
  return (
    <Box sx={{ overflow: 'hidden', mt: '4px', maxWidth: '100%' }}>
      <motion.div
        animate={{ x: reverse ? ['-50%', '0%'] : ['0%', '-50%'] }}
        transition={{ duration: Math.max(6, tags.length * 2.6), repeat: Infinity, ease: 'linear' }}
        style={{ display: 'flex', gap: 4, width: 'max-content' }}
      >
        {doubled.map((tag, i) => (
          <Box
            key={i}
            sx={{
              position: 'relative',
              px: '7px',
              py: '2px',
              borderRadius: '100px',
              flexShrink: 0,
              bgcolor:
                tag.kind === 'personality'
                  ? 'rgba(120, 90, 220, 0.45)'
                  : 'rgba(220, 80, 120, 0.45)',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                ...TEMP_TEXT_BOLD_XS,
                fontSize: 10,
                color: '#FFFFFF',
                whiteSpace: 'nowrap',
                filter: tag.locked ? 'blur(4px)' : 'none',
              }}
            >
              {tag.label.startsWith('#') ? tag.label : `#${tag.label}`}
            </Typography>
            {tag.locked && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="icLock" size={9} color="rgba(255,255,255,0.85)" />
              </Box>
            )}
          </Box>
        ))}
      </motion.div>
    </Box>
  );
}

function VoiceEqualizer({ color }: { color: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2.5px', height: 14 }}>
      {[0, 1, 2, 3].map((i) => (
        <motion.div
          key={i}
          animate={{ height: [4, 13, 4] }}
          transition={{
            duration: 0.85,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.12,
          }}
          style={{ width: 2.5, borderRadius: 2, background: color }}
        />
      ))}
    </Box>
  );
}

/** 공개 이미지들을 스르륵 크로스페이드로 순환 — 카드마다 시작 시점을 어긋나게 해서 자연스럽게 */
function CrossfadeImages({
  character,
  cardIndex,
}: {
  character: WorldCupCharacter;
  cardIndex: number;
}) {
  const paths = character.imagePaths?.length
    ? character.imagePaths
    : character.profileImagePath
      ? [character.profileImagePath]
      : [];
  const urls = paths
    .map((p) => getAssetUrl(p, 'large'))
    .filter(Boolean) as string[];
  const urlsKey = urls.join('|');
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (urls.length <= 1) return;
    // 미리 로드해서 전환 시 깜빡임 방지
    urls.forEach((u) => {
      const im = new window.Image();
      im.src = u;
    });
    let interval: ReturnType<typeof setInterval> | undefined;
    const kickoff = setTimeout(() => {
      setIdx((i) => (i + 1) % urls.length);
      interval = setInterval(() => setIdx((i) => (i + 1) % urls.length), 6500);
    }, 3000 + cardIndex * 900);
    return () => {
      clearTimeout(kickoff);
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlsKey, cardIndex]);

  if (urls.length === 0) {
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 32 }}>👤</Typography>
      </Box>
    );
  }

  return (
    <AnimatePresence initial={false}>
      <motion.img
        key={urls[idx % urls.length]}
        src={urls[idx % urls.length]}
        alt={displayName(character)}
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.6, ease: 'easeInOut' }}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    </AnimatePresence>
  );
}

interface CandidateCardProps {
  character: WorldCupCharacter;
  index: number;
  isSelected: boolean;
  isDimmed: boolean;
  isVoicePlaying?: boolean;
  onSelect: () => void;
  colors: ReturnType<typeof import('@/hooks/useDarkColors').useDarkColors>;
}

function CandidateCard({
  character,
  index,
  isSelected,
  isDimmed,
  isVoicePlaying,
  onSelect,
  colors: c,
}: CandidateCardProps) {
  const name = displayName(character);
  const isAdultVerified = useAuthStore((s) => s.user?.isAdultVerified ?? false);
  const isSafetyMode = useAuthStore((s) => s.user?.isSafetyMode ?? true);
  // 성인인증 완료 + 세이프티모드 해제 유저에게만 첫 번째 성향 태그 노출
  const tags = extractCardTags(character, isAdultVerified && !isSafetyMode);
  // 태그 4개 이상이면 두 줄로 나눠 서로 반대 방향으로 슬라이드
  const tagRows: [CardTag[], CardTag[]] =
    tags.length >= 4
      ? [tags.filter((_, i) => i % 2 === 0), tags.filter((_, i) => i % 2 === 1)]
      : [tags, []];

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{
        opacity: isDimmed ? 0.45 : 1,
        y: 0,
        scale: isSelected ? 1.02 : isDimmed ? 0.98 : 1,
      }}
      transition={{
        opacity: { duration: 0.25, ease: EASING },
        y: { duration: 0.3, ease: EASING, delay: index * 0.05 },
        scale: { duration: 0.25, ease: EASING },
      }}
    >
      <Box
        onClick={onSelect}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '3 / 3.8',
          borderRadius: '16px',
          overflow: 'hidden',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          bgcolor: c.bgInset,
          border: isSelected ? `2px solid ${c.primary}` : `1px solid ${c.border}`,
          boxShadow: isSelected
            ? `0 0 14px ${c.isDark ? 'rgba(124,58,237,0.25)' : 'rgba(75,0,204,0.1)'}`
            : 'none',
          transition: 'border 0.2s, box-shadow 0.2s',
          '&:active': { transform: 'scale(0.97)' },
        }}
      >
        <CrossfadeImages character={character} cardIndex={index} />

        {/* Selected check */}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.18, ease: EASING }}
              style={{ position: 'absolute', top: 8, right: 8 }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: c.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isVoicePlaying ? (
                  <Icon name="icVolume" size={12} color="#FFFFFF" />
                ) : (
                  <Icon name="icCheck" size={12} color="#FFFFFF" />
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom gradient + name */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            pt: '46px',
            pb: '9px',
            px: '10px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <Typography
              sx={{
                ...TEMP_TITLE_BOLD_M,
                fontSize: 15,
                color: '#FFFFFF',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </Typography>
            {character.age && (
              <Typography
                sx={{ ...TEMP_TEXT_MEDIUM_XS, color: 'rgba(255,255,255,0.75)' }}
              >
                {character.age}
              </Typography>
            )}
            {character.mbti && (
              <Typography
                sx={{
                  ...TEMP_TEXT_BOLD_XS,
                  color: 'rgba(255,255,255,0.6)',
                  letterSpacing: '0.5px',
                }}
              >
                · {character.mbti}
              </Typography>
            )}
          </Box>
          {tagRows[0].length > 0 && <TagMarquee tags={tagRows[0]} />}
          {tagRows[1].length > 0 && <TagMarquee tags={tagRows[1]} reverse />}
        </Box>
      </Box>
    </motion.div>
  );
}
